<template>
  <custom-modal
    id="video-manual"
    ref="forgotPasswordModalRef"
    v-b-modal.modal-center
    :title="'Manual De usuario'"
    @accept="() => {}"
  >
    <div>
      <video
        src="../../../public/manual.mp4"
        type="video/mp4"
        controls
        style="width: 100%"
      ></video>
    </div>
  </custom-modal>
</template>

<script>
//components
import { CustomModal } from '@/components/common';
import NavigationHeader from '@/components/common/tabs-navigation/NavigationHeader.vue';
import NavigationFooter from '@/components/common/tabs-navigation/NavigationFooter.vue';
import {
  SigninHeaderPanel,
  UserProfile,
  SecurityQuestion,
  SetNewPassword,
  ForgotPasswordFinalize,
} from './components';

//services
import {
  getForgotPasswordToken as _getForgotPasswordToken,
  getUserSecurityQuestion as _getUserSecurityQuestion,
  validateSecurityQuestion as _validateSecurityQuestion,
  setNewPassword as _setNewPassword,
} from '@/services/auth.api.js';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';
import { i18n } from '@/i18n';
import lang from '@/utils/constants/lang';

//constants
const STEPS = {
  USER_IDENTIFICATION: 1,
  SECURITY_QUESTION: 2,
  SET_NEW_PASSWORD: 3,
  FINALIZE: 4,
};

export default {
  name: 'ManualVideo',
  mixins: [alertMixin],
  components: {
    CustomModal,
    NavigationHeader,
    NavigationFooter,
    SigninHeaderPanel,
    UserProfile,
    SecurityQuestion,
    SetNewPassword,
    ForgotPasswordFinalize,
  },
  data: function () {
    return {
      currentStepId: STEPS.USER_IDENTIFICATION,
      model: {
        userName: null,
        userId: null,
        securityCode: null,
        securityToken: null,
        securityQuestion: null,
      },
    };
  },
  computed: {
    steps() {
      return [
        {
          id: STEPS.USER_IDENTIFICATION,
          title: i18n.t('lblIdentifyUser'),
          component: UserProfile,
        },
        {
          id: STEPS.SECURITY_QUESTION,
          title: this.$options.filters.capitalize(
            i18n.t('lblSecurityQuestion')
          ),
          component: SecurityQuestion,
        },
        {
          id: STEPS.SET_NEW_PASSWORD,
          title: i18n.t('lblNewPassword'),
          component: SetNewPassword,
        },
        {
          id: STEPS.FINALIZE,
          title: i18n.t('lblFinish'),
          component: ForgotPasswordFinalize,
        },
      ];
    },
    currentStep() {
      return this.steps.find((x) => x.id === this.currentStepId);
    },
  },
  methods: {
    showModal() {
      this.currentStepId = STEPS.USER_IDENTIFICATION;
      this.$refs.forgotPasswordModalRef.showModal();
    },
    hideModal() {
      this.resetModel();
      this.$refs.forgotPasswordModalRef.hideModal();
    },
    onCancelHandler() {
      this.resetModel();
      this.$emit('cancel');
    },
    onFinishHandler() {
      this.$emit('finish');
    },
    getCurrentStepModel() {
      return this.$refs.currentTabRef.getModel();
    },
    async setCurrentStepModel(values) {
      await this.$nextTick(() => {
        this.$refs.currentTabRef.setModel(values);
      });
    },
    resetModel() {
      this.model = {};
    },
    async onPrevHandler() {
      //clear step model
      await this.setCurrentStepModel({
        question: null,
        answer: null,
        newPassword: null,
        newPasswordConfirm: null,
      });
      this.currentStepId -= 1;
    },
    async onNextHandler() {
      //validate all required fields
      const allFieldsAreCompleted = await this.validateForm();
      if (!allFieldsAreCompleted) {
        return;
      }

      //get current step model
      const stepModel = this.getCurrentStepModel();

      //validate username and get security question
      if (this.currentStepId === STEPS.USER_IDENTIFICATION) {
        const isValid = await this.validateUsernameAndGetSecurityQuestion(
          stepModel.userName
        );
        if (!isValid) {
          return;
        }
        this.model.userName = stepModel.userName;
      }

      //validate security answer
      if (this.currentStepId === STEPS.SECURITY_QUESTION) {
        const isValid = await this.validateSecurityAnswer(
          this.model.userName,
          stepModel.answer
        );
        if (!isValid) {
          return;
        }
      }

      if (this.currentStepId === STEPS.SET_NEW_PASSWORD) {
        const isValid = await this.setNewPassword(
          this.model.userId,
          stepModel.newPassword,
          stepModel.newPasswordConfirm,
          this.model.securityCode,
          this.model.securityToken
        );
        if (!isValid) {
          return;
        }
      }

      //move to next step
      this.currentStepId += 1;

      //set security question after move to the next step
      if (this.currentStepId === STEPS.SECURITY_QUESTION) {
        await this.setCurrentStepModel({
          question: this.model.securityQuestion,
        });
      }
    },
    async validateForm() {
      if (this.$refs.currentTabRef.validate) {
        const isValid = await this.$refs.currentTabRef.validate();
        if (!isValid) {
          this.ShowToast(
            i18n.t('Alert'),
            `${i18n.t('PleaseCompleteRequestedInformation')}.`,
            'error'
          );
          return;
        }
        return true;
      }
    },
    async validateUsernameAndGetSecurityQuestion(userName) {
      //get security token
      const forgotPassResponse = (await _getForgotPasswordToken(userName)).data;
      if (!(forgotPassResponse || {}).isValid) {
        this.ShowToast(
          'Error',
          this.userSelectedLang === lang.ES
            ? forgotPassResponse.message
            : forgotPassResponse.messageEn,
          'error'
        );
        return false;
      }

      //get security question
      const questionResponse = (await _getUserSecurityQuestion(userName)).data;
      if (!(questionResponse || {}).isValid) {
        this.ShowToast('Error', questionResponse.message, 'error');
        return false;
      }

      //store security token and question
      this.model = {
        ...this.model,
        userId: questionResponse.userId,
        securityToken: forgotPassResponse.token,
        securityCode: forgotPassResponse.code,
        securityQuestion: questionResponse.question,
      };

      return true;
    },
    async validateSecurityAnswer(userName, answer) {
      const response = (await _validateSecurityQuestion(userName, answer)).data;
      if (!(response || {}).isValid) {
        this.ShowToast(
          'Error',
          this.userSelectedLang === lang.ES
            ? response.message
            : response.messageEn,
          'error'
        );
        return false;
      }
      return true;
    },
    async setNewPassword(userId, password, passworConfirm, code, token) {
      const response = (
        await _setNewPassword(userId, password, passworConfirm, code, token)
      ).data;

      if (!(response || {}).isValid) {
        this.ShowToast('Error', response.message, 'error');
        return false;
      }
      return true;
    },
  },
};
</script>
